import React, { useEffect, useState } from 'react';
import { Card, Input, Button, Space, Typography, Descriptions, Tag, message, Spin, Alert } from 'antd';
import { SearchOutlined, CheckCircleOutlined, FormOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import assetService, { type AssetDto } from '@/services/asset.service';

const { Title, Text } = Typography;

const statusColor: Record<string, string> = {
  draft: 'default',
  active: 'green',
  in_use: 'blue',
  under_maintenance: 'orange',
  idle: 'gold',
  disposed: 'red',
};

const AssetScanPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const canVerify = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.view'));
  const canMove = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.move'));
  const [code, setCode] = useState('');
  const [result, setResult] = useState<AssetDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refFromUrl = searchParams.get('ref') || searchParams.get('code');

  const verifyMut = useMutation({
    mutationFn: (id: string) => assetService.recordVerificationScan(id),
    onSuccess: (a) => {
      message.success('Physical verification saved');
      setResult(a);
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['asset', a.id] });
    },
    onError: (e: Error) => message.error(e?.message || 'Failed to record verification'),
  });

  const runLookup = (raw: string) => {
    const t = (raw || '').trim();
    if (!t) {
      setErr('Enter a barcode, asset code, serial number, or paste a full QR or URL from the label.');
      return;
    }
    setLoading(true);
    setErr(null);
    setResult(null);
    void assetService
      .lookupAsset(t)
      .then((a) => {
        setResult(a);
      })
      .catch((e) => {
        setErr((e as Error)?.message || 'No asset found');
        setResult(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (refFromUrl) {
      setCode(refFromUrl);
      setLoading(true);
      setErr(null);
      setResult(null);
      void assetService
        .lookupAsset(refFromUrl)
        .then((a) => {
          setResult(a);
        })
        .catch((e) => {
          setErr((e as Error)?.message || 'No asset found');
          setResult(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [refFromUrl]);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/assets')}>
        Back to list
      </Button>
      <Title level={4} style={{ marginTop: 8 }}>
        Scan or find asset
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Type or paste what the scanner or camera reads (barcode, code, URL from a label), then look up. Use
        &quot;Record verification&quot; to confirm a physical check on site.
      </Text>
      <Space size="large" style={{ display: 'flex', marginBottom: 16, flexWrap: 'wrap' }}>
        <Text type="secondary">
          <Link to="/asset/audit">Physical stocktake / audit cycles</Link>
        </Text>
        <Text type="secondary">
          <Link to="/asset/audit/report">Audit summary report</Link>
        </Text>
      </Space>
      {refFromUrl && <Alert type="info" showIcon message="Code loaded from link" style={{ marginBottom: 12 }} closable />}

      <Input.Search
        placeholder="Barcode, asset code, serial, withu-asset|…, or QR URL"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onSearch={(v) => {
          const t = (v && String(v).trim()) || code.trim();
          runLookup(t);
        }}
        enterButton={<SearchOutlined />}
        size="large"
        allowClear
      />
      {loading && (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      )}

      {err && !loading && <Alert type="error" showIcon message={err} style={{ marginTop: 16 }} />}

      {result && !loading && (
        <Card
          size="small"
          style={{ marginTop: 16 }}
          title={
            <span>
              {result.asset_name}{' '}
              <Tag color={statusColor[result.status] || 'default'} style={{ textTransform: 'none' as const }}>
                {String(result.status || '').replace(/_/g, ' ')}
              </Tag>
            </span>
          }
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Code">{result.asset_code}</Descriptions.Item>
            {result.barcode && <Descriptions.Item label="Barcode">{result.barcode}</Descriptions.Item>}
            {result.last_verified_at && (
              <Descriptions.Item label="Last physical verification">
                {dayjs(result.last_verified_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Location">
              {[result.site, result.building, result.room].filter(Boolean).join(' · ') || '—'}
            </Descriptions.Item>
          </Descriptions>
          <Space wrap style={{ marginTop: 12 }}>
            <Button type="primary" icon={<FormOutlined />} onClick={() => navigate(`/asset/assets/${result.id}/edit`)}>
              Open in register
            </Button>
            {canVerify && (
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => void verifyMut.mutateAsync(result.id)}
                loading={verifyMut.isPending}
              >
                Record physical verification
              </Button>
            )}
            {canMove && (result.status === 'in_use' || result.status === 'active' || result.status === 'idle') && (
              <Button onClick={() => void navigate(`/asset/assets/${result.id}/edit`)}>Manage movements (edit)</Button>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default AssetScanPage;
